import {
  Flex,
  Container,
  Box,
  Image,
  Text,
  Button,
  HStack,
  VStack,
  Icon,
  useDisclosure,
  useBreakpointValue,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc } from "firebase/firestore";
import {
  IoSparklesOutline,
  IoArrowForwardOutline,
  IoStorefrontOutline,
  IoFlashOutline,
  IoCheckmarkCircleOutline,
  IoTicketOutline,
  IoPeopleOutline,
  IoTrendingUpOutline,
  IoCalendarOutline,
  IoStarOutline,
  IoCashOutline,
} from "react-icons/io5";
import { auth, db } from "../firebase/firebase-config";
import item1 from "../Assets/Images/item1.png";
import item2 from "../Assets/Images/item2.png";
import item3 from "../Assets/Images/item3.png";
import {
  CreateEventModal,
  SignInModal,
  BecomeHostModal,
  HowItWorksModal,
} from "../Components/Modals";

const HERO_IMAGES = [item1, item2, item3];

// Each hero "scene" pairs a background image with content for the two
// floating chips and the live-status pill. We render every scene
// stacked and toggle opacity via plain CSS transitions — no
// AnimatePresence, no mount/unmount work each tick. That's what kills
// jank on mobile.
const SCENES = [
  {
    statusColor: "#22c55e",
    statusLabel: "Selling Now",
    top: {
      icon: IoStorefrontOutline,
      iconBg: "blue.50",
      iconColor: "#508cf7",
      eyebrow: "Partner Venue",
      title: "Confirmed",
      footerIcon: IoCheckmarkCircleOutline,
      footerIconColor: "#22c55e",
      footerText: "Minimum met — event locked in.",
    },
    bottom: {
      icon: IoTicketOutline,
      iconBg: "linear(to-br, #508cf7, #226cf1)",
      iconColor: "white",
      eyebrow: "Tickets sold",
      title: "82 / 100",
      badgeIcon: IoPeopleOutline,
      badgeText: "+8",
      badgeColor: "green.500",
    },
  },
  {
    statusColor: "#508cf7",
    statusLabel: "Going live",
    top: {
      icon: IoCalendarOutline,
      iconBg: "purple.50",
      iconColor: "#a855f7",
      eyebrow: "Event draft",
      title: "Auto-saved",
      footerIcon: IoFlashOutline,
      footerIconColor: "#a855f7",
      footerText: "Ready to publish in one tap.",
    },
    bottom: {
      icon: IoPeopleOutline,
      iconBg: "linear(to-br, #22c55e, #16a34a)",
      iconColor: "white",
      eyebrow: "Guests",
      title: "42 going",
      badgeIcon: IoTrendingUpOutline,
      badgeText: "+12",
      badgeColor: "green.500",
    },
  },
  {
    statusColor: "#f59e0b",
    statusLabel: "Trending",
    top: {
      icon: IoStarOutline,
      iconBg: "orange.50",
      iconColor: "#f59e0b",
      eyebrow: "Host rating",
      title: "4.9 / 5",
      footerIcon: IoSparklesOutline,
      footerIconColor: "#f59e0b",
      footerText: "Loved by 3,200+ guests.",
    },
    bottom: {
      icon: IoCashOutline,
      iconBg: "linear(to-br, #ec4899, #db2777)",
      iconColor: "white",
      eyebrow: "Revenue",
      title: "$1,840",
      badgeIcon: IoTrendingUpOutline,
      badgeText: "+24%",
      badgeColor: "green.500",
    },
  },
];

// CSS that the cross-fade and float animations rely on. Defined once
// at the document level so the browser can compile/optimize it once
// instead of re-parsing per element.
const HERO_STYLES = `
  .proximy-hero-layer {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 1s ease;
    will-change: opacity;
    backface-visibility: hidden;
    transform: translate3d(0,0,0);
  }
  .proximy-hero-layer.is-active { opacity: 1; }

  .proximy-chip-layer {
    position: absolute;
    inset: 12px;
    opacity: 0;
    transition: opacity 0.45s ease;
    will-change: opacity;
    backface-visibility: hidden;
  }
  .proximy-chip-layer.is-active { opacity: 1; }

  @keyframes proximyGradient {
    0%   { background-position:   0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position:   0% 50%; }
  }
  @keyframes proximyPulse {
    0%   { box-shadow: 0 0 0 0 var(--proximy-status, rgba(34,197,94,0.4)); }
    70%  { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
    100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
  }
  @keyframes proximyFloatA {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(0, -6px, 0); }
  }
  @keyframes proximyFloatB {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50%      { transform: translate3d(0, 6px, 0); }
  }
  @keyframes proximyHeroIn {
    from { opacity: 0; transform: translate3d(0, 12px, 0); }
    to   { opacity: 1; transform: translate3d(0, 0, 0); }
  }
  .proximy-fade-in {
    animation: proximyHeroIn 0.55s ease-out both;
    will-change: opacity, transform;
  }
  @media (prefers-reduced-motion: reduce) {
    .proximy-hero-layer,
    .proximy-chip-layer { transition: none; }
    .proximy-fade-in { animation: none; }
  }
`;

export const Landing = () => {
  const [index, setIndex] = useState(0);
  // Mobile: lock to a single scene + static visual. Animation work was
  // the source of the perceived jank on phones; tablet/desktop are
  // headroomed enough to do the full carousel smoothly.
  const shouldRotateHero = useBreakpointValue({ base: false, md: true });
  const activeIndex = shouldRotateHero ? index : 0;

  const [user] = useAuthState(auth);
  const [isHost, setIsHost] = useState(false);
  const { isOpen: isEventModalOpen, onOpen: onEventModalOpen, onClose: onEventModalClose } = useDisclosure();
  const { isOpen: isHostModalOpen, onOpen: onHostModalOpen, onClose: onHostModalClose } = useDisclosure();
  const { isOpen: isSignInModalOpen, onOpen: onSignInModalOpen, onClose: onSignInModalClose } = useDisclosure();
  const { isOpen: isHowItWorksOpen, onOpen: onHowItWorksOpen, onClose: onHowItWorksClose } = useDisclosure();

  // Preload + decode all hero images on mount so the cross-fade never
  // waits on network or decode work mid-frame.
  useEffect(() => {
    HERO_IMAGES.forEach((src) => {
      const img = new window.Image();
      img.src = src;
      if (img.decode) {
        img.decode().catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    if (!shouldRotateHero) return undefined;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [shouldRotateHero]);

  useEffect(() => {
    const fetchHostStatus = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) setIsHost(userDoc.data().isHost || false);
        } catch (error) {
          console.error("Error fetching host status:", error);
        }
      }
    };
    fetchHostStatus();
  }, [user]);

  const handleCtaClick = () => {
    if (!user) onSignInModalOpen();
    else if (!isHost) onHostModalOpen();
    else onEventModalOpen();
  };

  const featureChips = [
    { icon: IoFlashOutline, label: "Launch in minutes" },
    { icon: IoStorefrontOutline, label: "Partner venues" },
    { icon: IoCheckmarkCircleOutline, label: "Zero upfront cost" },
  ];

  return (
    <Box
      minH="calc(100vh - 62px)"
      width="100%"
      overflow="hidden"
      position="relative"
      bg="white"
    >
      {/* Inline the keyframes/transition rules once — keeps Chakra's
          per-element sx parser out of the hot path. */}
      <style>{HERO_STYLES}</style>

      {/* Decorative blurred orbs — desktop only. Large filter:blur layers
          are static but still hold big GPU textures, which on phone
          GPUs causes scroll/animation jank. We skip them on mobile. */}
      <Box
        aria-hidden
        display={{ base: "none", md: "block" }}
        position="absolute"
        top="-160px"
        left="-120px"
        w="420px"
        h="420px"
        bg="#508cf7"
        opacity={0.18}
        filter="blur(120px)"
        borderRadius="full"
        pointerEvents="none"
        zIndex={0}
      />
      <Box
        aria-hidden
        display={{ base: "none", md: "block" }}
        position="absolute"
        bottom="-180px"
        right="-140px"
        w="460px"
        h="460px"
        bg="#ff6b9d"
        opacity={0.12}
        filter="blur(140px)"
        borderRadius="full"
        pointerEvents="none"
        zIndex={0}
      />

      <Container
        maxW="container.xl"
        minH="calc(100vh - 62px)"
        px={{ base: "20px", md: "30px", lg: "60px" }}
        position="relative"
        zIndex={1}
      >
        <Flex
          alignItems="center"
          w="full"
          minH="calc(100vh - 62px)"
          justifyContent="space-between"
          py={{ base: "40px", md: "60px" }}
          gap={{ base: "40px", md: "60px", lg: "80px" }}
          direction={{ base: "column", lg: "row" }}
        >
          {/* ── Left: copy + CTA ───────────────────────────────────── */}
          <Flex
            className="proximy-fade-in"
            textAlign="start"
            flexDirection="column"
            flex="1"
            maxW={{ base: "100%", lg: "55%" }}
            w="full"
          >
            <HStack
              spacing={2}
              bg="blue.50"
              color="#226cf1"
              borderRadius="full"
              px={3}
              py={1.5}
              alignSelf="flex-start"
              mb={5}
              border="1px solid"
              borderColor="blue.100"
            >
              <Icon as={IoSparklesOutline} boxSize="14px" />
              <Text fontSize="12px" fontWeight="700" letterSpacing="0.02em">
                Now with Partner Venues
              </Text>
            </HStack>

            <Text
              className="font-gilroy-bold"
              fontSize={{ base: "44px", md: "58px", lg: "68px" }}
              lineHeight={{ base: "52px", md: "66px", lg: "76px" }}
              letterSpacing="-0.02em"
              whiteSpace="normal"
            >
              Create{" "}
              <Box
                as="span"
                sx={{
                  background:
                    "linear-gradient(270deg, #508cf7, #7b3cff, #ff3d71, #508cf7)",
                  // Animating background-clip: text + background-position
                  // forces a full text repaint on every frame. On phones
                  // that's the single most expensive thing we paint, so
                  // gate it on viewport — desktops only.
                  backgroundSize: shouldRotateHero ? "400% 400%" : "100% 100%",
                  animation: shouldRotateHero
                    ? "proximyGradient 8s ease infinite"
                    : "none",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  display: "inline",
                  willChange: shouldRotateHero ? "background-position" : "auto",
                }}
              >
                unforgettable
              </Box>{" "}
              experiences.
            </Text>

            <Text
              fontWeight="500"
              pt={5}
              fontSize={{ base: "15px", md: "17px" }}
              color="gray.500"
              lineHeight="1.65"
              maxW="540px"
            >
              Set up an event page, invite friends, sell tickets, and book a
              partner venue — all from one place. Host an unforgettable
              experience today.
            </Text>

            <HStack spacing={3} mt={8} flexWrap="wrap">
              <Button
                onClick={handleCtaClick}
                size="lg"
                bgGradient="linear(to-r, #508cf7, #226cf1)"
                color="white"
                fontSize="15px"
                fontWeight="700"
                borderRadius="14px"
                px={7}
                h="54px"
                rightIcon={<Icon as={IoArrowForwardOutline} boxSize="18px" />}
                boxShadow="0 10px 24px rgba(80,140,247,0.35)"
                _hover={{
                  bgGradient: "linear(to-r, #226cf1, #1d4ed8)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 14px 32px rgba(80,140,247,0.45)",
                }}
                _active={{ transform: "translateY(0)" }}
                style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}
              >
                {!user ? "Get Started" : !isHost ? "Become a Host" : "Create Your Event"}
              </Button>

              <Button
                onClick={onHowItWorksOpen}
                variant="ghost"
                size="lg"
                fontSize="14px"
                fontWeight="600"
                color="gray.700"
                borderRadius="14px"
                h="54px"
                px={5}
                _hover={{ bg: "gray.50", color: "#226cf1" }}
              >
                See how it works
              </Button>
            </HStack>

            <HStack spacing={2} mt={8} flexWrap="wrap">
              {featureChips.map(({ icon, label }) => (
                <HStack
                  key={label}
                  spacing={1.5}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.150"
                  borderRadius="full"
                  px={3}
                  py={1.5}
                  boxShadow="0 1px 3px rgba(0,0,0,0.04)"
                >
                  <Icon as={icon} boxSize="14px" color="#508cf7" />
                  <Text fontSize="12px" fontWeight="600" color="gray.600">
                    {label}
                  </Text>
                </HStack>
              ))}
            </HStack>
          </Flex>

          {/* ── Tablet/desktop: full hero with cross-fading image,
              status pill, and two floating chips. Every cross-fade
              is a stacked-layer opacity toggle (not a mount), so the
              compositor handles it on the GPU with zero layout work. */}
          <Flex
            display={{ base: "none", md: "flex" }}
            className="proximy-fade-in"
            flex="1"
            justifyContent={{ base: "center", lg: "flex-end" }}
            alignItems="center"
            maxW={{ base: "100%", lg: "45%" }}
            w="full"
            position="relative"
            sx={{ animationDelay: "0.15s" }}
          >
            <Box
              width="100%"
              maxW={{ base: "420px", md: "500px", lg: "560px" }}
              position="relative"
              overflow="visible"
            >
              {/* Soft static glow behind the card */}
              <Box
                aria-hidden
                position="absolute"
                inset={0}
                bgGradient="linear(to-br, #508cf7, #a855f7)"
                opacity={0.25}
                filter="blur(40px)"
                borderRadius="32px"
                transform="translateY(20px)"
                zIndex={0}
              />

              {/* Image card. All scenes are absolutely stacked; only
                  opacity changes per tick. The fixed height means
                  there's no layout shift while images preload. */}
              <Box
                position="relative"
                zIndex={1}
                borderRadius="28px"
                overflow="hidden"
                boxShadow="0 24px 60px rgba(15,23,42,0.18), 0 8px 20px rgba(15,23,42,0.08)"
                border="1px solid"
                borderColor="whiteAlpha.800"
                bg="white"
                h={{ base: "360px", md: "460px", lg: "520px" }}
              >
                {HERO_IMAGES.map((src, i) => (
                  <Box
                    key={src}
                    className={`proximy-hero-layer${i === activeIndex ? " is-active" : ""}`}
                  >
                    <Image
                      w="full"
                      h="full"
                      src={src}
                      alt=""
                      objectFit="cover"
                      loading={i === 0 ? "eager" : "lazy"}
                      decoding="async"
                      fetchpriority={i === 0 ? "high" : "auto"}
                      draggable={false}
                    />
                  </Box>
                ))}

                {/* Status pill — top-left. Stacked layers, opacity
                    cross-fade. No remount. */}
                <Box position="absolute" top={4} left={4} zIndex={2}>
                  {SCENES.map((s, i) => (
                    <Box
                      key={s.statusLabel}
                      position={i === 0 ? "relative" : "absolute"}
                      top={i === 0 ? undefined : 0}
                      left={i === 0 ? undefined : 0}
                      opacity={i === activeIndex ? 1 : 0}
                      transition="opacity 0.45s ease"
                      style={{ willChange: "opacity", backfaceVisibility: "hidden" }}
                    >
                      <HStack
                        spacing={2}
                        bg="whiteAlpha.900"
                        backdropFilter="blur(10px)"
                        borderRadius="full"
                        px={3}
                        py={1.5}
                        boxShadow="0 4px 12px rgba(0,0,0,0.12)"
                      >
                        <Box
                          w="7px"
                          h="7px"
                          borderRadius="full"
                          bg={s.statusColor}
                          sx={{
                            "--proximy-status": `${s.statusColor}66`,
                            animation: "proximyPulse 1.6s ease-out infinite",
                          }}
                        />
                        <Text
                          fontSize="11px"
                          fontWeight="700"
                          color="gray.800"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          whiteSpace="nowrap"
                        >
                          {s.statusLabel}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Floating top-right chip. The outer box bobs (pure CSS,
                  GPU-only). Inside, all 3 scene contents are stacked
                  and opacity-toggled — so the chip never reflows. */}
              <Box
                position="absolute"
                top="20px"
                right="-24px"
                bg="white"
                borderRadius="16px"
                border="1px solid"
                borderColor="gray.100"
                p={3}
                boxShadow="0 12px 30px rgba(15,23,42,0.12)"
                w="220px"
                h="92px"
                zIndex={2}
                sx={{
                  willChange: "transform",
                  animation: "proximyFloatA 5.5s ease-in-out infinite",
                }}
              >
                {SCENES.map((s, i) => (
                  <Box
                    key={`top-${i}`}
                    className={`proximy-chip-layer${i === activeIndex ? " is-active" : ""}`}
                  >
                    <HStack spacing={2} mb={2} align="center">
                      <Box
                        w="28px"
                        h="28px"
                        borderRadius="lg"
                        bg={s.top.iconBg}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Icon
                          as={s.top.icon}
                          color={s.top.iconColor}
                          boxSize="14px"
                        />
                      </Box>
                      <VStack align="flex-start" spacing={0} minW={0} flex={1}>
                        <Text
                          fontSize="10px"
                          color="gray.400"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                        >
                          {s.top.eyebrow}
                        </Text>
                        <Text fontSize="12px" fontWeight="700" color="gray.800">
                          {s.top.title}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={1} align="flex-start">
                      <Icon
                        as={s.top.footerIcon}
                        color={s.top.footerIconColor}
                        boxSize="12px"
                        mt="2px"
                        flexShrink={0}
                      />
                      <Text fontSize="11px" color="gray.500" lineHeight="1.35">
                        {s.top.footerText}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </Box>

              {/* Floating bottom-left chip — same idea, offset bob. */}
              <Box
                position="absolute"
                bottom="30px"
                left="-28px"
                bg="white"
                borderRadius="16px"
                border="1px solid"
                borderColor="gray.100"
                p={3}
                boxShadow="0 12px 30px rgba(15,23,42,0.12)"
                w="220px"
                h="70px"
                zIndex={2}
                sx={{
                  willChange: "transform",
                  animation: "proximyFloatB 6.5s ease-in-out infinite",
                }}
              >
                {SCENES.map((s, i) => (
                  <Box
                    key={`bottom-${i}`}
                    className={`proximy-chip-layer${i === activeIndex ? " is-active" : ""}`}
                  >
                    <HStack justify="space-between" spacing={3} h="full">
                      <HStack spacing={2} minW={0} flex={1}>
                        <Box
                          w="28px"
                          h="28px"
                          borderRadius="lg"
                          bgGradient={s.bottom.iconBg}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Icon
                            as={s.bottom.icon}
                            color={s.bottom.iconColor}
                            boxSize="14px"
                          />
                        </Box>
                        <VStack align="flex-start" spacing={0} minW={0} flex={1}>
                          <Text
                            fontSize="10px"
                            color="gray.400"
                            fontWeight="600"
                            textTransform="uppercase"
                            letterSpacing="0.05em"
                          >
                            {s.bottom.eyebrow}
                          </Text>
                          <Text
                            fontSize="14px"
                            fontWeight="800"
                            color="gray.800"
                          >
                            {s.bottom.title}
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={1} color={s.bottom.badgeColor} flexShrink={0}>
                        <Icon as={s.bottom.badgeIcon} boxSize="12px" />
                        <Text fontSize="11px" fontWeight="700">
                          {s.bottom.badgeText}
                        </Text>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </Box>
            </Box>
          </Flex>
        </Flex>
      </Container>

      {/* Footer — quiet legal links, always present. backdrop-filter
          is desktop-only because phone browsers re-paint it on every
          scroll position which adds noticeable jank. */}
      <Box
        position="relative"
        zIndex={1}
        borderTop="1px solid"
        borderColor="gray.100"
        bg="white"
        sx={{
          "@media (min-width: 768px)": {
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <Container
          maxW="container.xl"
          px={{ base: "20px", md: "30px", lg: "60px" }}
          py={4}
        >
          <Flex
            direction={{ base: "column", sm: "row" }}
            align={{ base: "flex-start", sm: "center" }}
            justify="space-between"
            gap={2}
          >
            <Text fontSize="11px" color="gray.400">
              &copy; {new Date().getFullYear()} Proximy Technologies, Inc.
            </Text>
            <HStack spacing={4} color="gray.400" fontSize="11px">
              <Box
                as="a"
                href="https://proximy-site.web.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                _hover={{ color: "#508cf7", textDecoration: "underline" }}
              >
                Terms
              </Box>
              <Box
                as="a"
                href="https://proximy-site.web.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                _hover={{ color: "#508cf7", textDecoration: "underline" }}
              >
                Privacy
              </Box>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <CreateEventModal isOpen={isEventModalOpen} onClose={onEventModalClose} user={user} />
      <BecomeHostModal isOpen={isHostModalOpen} onClose={onHostModalClose} user={user} setIsHost={setIsHost} />
      <SignInModal isOpen={isSignInModalOpen} onClose={onSignInModalClose} />
      <HowItWorksModal isOpen={isHowItWorksOpen} onClose={onHowItWorksClose} />
    </Box>
  );
};
